import { ReactNode, HTMLAttributes } from "react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "../typography";

type Props = HTMLAttributes<HTMLDivElement> & {
  title: string;
  description?: string;
  imageComponent: ReactNode;
  badgeComponent?: ReactNode;
};

/**
 * Panel component for displaying creator tools with image, title, description and optional badge.
 * Wrap with Link or add onClick as needed - this component just renders the content.
 *
 * @example
 * ```tsx
 * import { StatusBadge } from "@drpcrd/shared/ui";
 *
 * // With onClick
 * <CreatorToolPanel
 *   title="Link in Bio"
 *   description="Create your personalized landing page"
 *   imageComponent={<Image alt="" src={hi} fill objectFit="cover" />}
 *   badgeComponent={<StatusBadge label="Active" status="error" />}
 *   onClick={() => router.push("/tools/link-in-bio")}
 * />
 *
 * // With Next.js Link
 * <Link href="/tools/link-in-bio">
 *   <CreatorToolPanel
 *     title="Link in Bio"
 *     description="Create your personalized landing page"
 *     imageComponent={<Image alt="" src={hi} fill objectFit="cover" />}
 *     badgeComponent={<StatusBadge label="Active" status="success" />}
 *   />
 * </Link>
 * ```
 */
export const CreatorToolPanel = ({
  title,
  description,
  imageComponent,
  badgeComponent,
  className,
  ...props
}: Props) => (
  <div
    className={cn(
      "bg-bg-primary flex h-fit w-full cursor-pointer flex-col gap-0 overflow-hidden rounded-lg",
      className,
    )}
    {...props}
  >
    {/* Image */}
    <div className="relative aspect-[100/41] h-19 overflow-hidden">
      {imageComponent}
    </div>

    {/* Labels */}
    <div className="border-border-primary flex h-fit flex-col justify-center rounded-lg rounded-t-none border border-t-0 px-2 py-1.5">
      <Typography weight="semibold" className="leading-2.75" truncate>
        {title}
      </Typography>
      {description && (
        <Typography color="neutral" className="line-clamp-2 leading-2.75">
          {description}
        </Typography>
      )}

      {/* Badge */}
      {badgeComponent}
    </div>
  </div>
);
